import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { useSelector } from "react-redux";
import useStyles from "./ChoiceListRenderer.styles";
import {
    carrouselScaleChoicesDomWrapped,
} from "../../../models/utilities/carrouselUtilities";
import {
    computeCarrouselScaleLayoutPlan,
    getChoiceLabelPlainText,
    type CarrouselScaleLayoutPlan,
} from "../../../models/utilities/carrouselScaleLayoutUtilities";
import CarrouselScaleItemRenderer from "./CarrouselScaleItemRenderer";
import type { CarrouselScaleAnchorLabelIds } from "./carrouselScaleA11y";
import { RendererProps, getRendererProps } from "../BaseRenderer";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import ChoiceListItemRenderer from "./ChoiceListItemRenderer";
import { MentionModel } from "../../../models/MentionModel";
import { CheckBox } from "../../choices/CheckBox";
import { Radio } from "../../choices/Radio";
import { ImageSelect } from "../../choices/ImageSelect";
import {
    getSettingBoolean,
    getSettingNumber,
    getSettingString,
} from "../../../models/utilities/settingUtilities";
import { VariableModel } from "../../../models/VariableModel";
import Button from "../../shared/Button";
import useQuestion from "../../../hooks/useQuestion";
import useTranslatable from "../../../hooks/useTranslatable";
import { QuestionModel } from "../../../models/QuestionModel";
import { getChoiceListDisplayType } from "../../../models/utilities/questionUtilities";
import { ChoiceListDisplayType } from "../../../models/enums/ChoiceListDisplayType";
import useDimensionContext from "../../../hooks/useDimensionContext";
import { isIE, isNullOrEmpty } from "../../../utils";
import { getRoleForQuestion, stripHtmlForAccessibleName } from "../../../utils/WCAGHelper";
import { Theme } from "../../../types/Theme";
import { StoreState } from "../../../models/states/StoreState";
import { translate } from "../../../models/utilities/translationUtilities";
import { hasChoiceValidationError } from "../../../models/utilities/choiceValidationUtilities";
import { shouldUseModernTheme } from "../../../theme/navigationButtonThemeColors";

interface Props extends RendererProps {
    variable: VariableModel;
    openEndValue?: { [key: string]: string };
    onClick?(choice: ChoiceModel): void;
    onOpenEndChange?(choice: ChoiceModel, text: string): void;
    onOpenEndBlur?(choice: ChoiceModel): void;
    mentions: MentionModel[];
    choices: ChoiceModel[];
    gridRendering?: boolean;
    onReset(): void;
    language: string;
    maxAnswers?: number;
    isSelected?: boolean;
    isCarrouselCheckboxActive?: boolean;
    isCarrouselScaleActive?: boolean;
    onAfterSelection?(): void;
    executeUpdateForIdAfterSelection?: string;
}

export type { CarrouselScaleAnchorLabelIds } from "./carrouselScaleA11y";

const shouldDisplayResetButton = (question: QuestionModel) =>
    getSettingBoolean(question, "DisplayResetButton");

function isSelected(props: Props, choice: ChoiceModel) {
    const { mentions } = props;
    return mentions.some((mention) => mention.value === choice.value);
}

function isChoiceDisabled(props: Props, selected: boolean, exclusive: boolean, controlType: any) {
    const { question, maxAnswers, mentions } = props;
    if (controlType === Radio) {
        return false;
    }
    if (maxAnswers === -1) {
        return false;
    }
    if (question.type === QuestionType.ImageSelector && maxAnswers === 1) {
        return false;
    }
    return !selected && !exclusive && mentions.length >= maxAnswers;
}

function renderChoiceListItem(
    props: Props,
    choice: ChoiceModel,
    isFirst: boolean,
    isLast: boolean,
    key: any,
    controlType: any,
    choiceIndex: number,
    choiceCount: number,
    scaleAnchorLabelIds?: CarrouselScaleAnchorLabelIds,
    horizontalChoices?: boolean,
    isCarrouselScaleHorizontalRow?: boolean,
) {
    const {
        question,
        gridRendering,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
        variable,
        onAfterSelection,
        executeUpdateForIdAfterSelection,
        isCarrouselCheckboxActive,
        isCarrouselScaleActive,
    } = props;
    const displayType = getChoiceListDisplayType(question);
    const isHorizontalDisplay =
        horizontalChoices ??
        (displayType === ChoiceListDisplayType.Horizontal ||
            displayType === ChoiceListDisplayType.HorizontalColumns);
    const selected = isSelected(props, choice);
    const exclusive = getSettingBoolean(choice, "Exclusive");
    const isDisabled = isChoiceDisabled(props, selected, exclusive, controlType);
    return (
        <ChoiceListItemRenderer
            key={key}
            {...getRendererProps(props)}
            controlType={controlType}
            gridRendering={gridRendering}
            isFirst={isFirst}
            isLast={isLast}
            openEnd={(openEndValue || {})[choice.index]}
            onOpenEndChange={onOpenEndChange}
            onOpenEndBlur={onOpenEndBlur}
            selected={selected}
            choice={choice}
            variable={variable}
            disabled={isDisabled}
            horizontal={isHorizontalDisplay}
            onAfterSelection={onAfterSelection}
            executeUpdateForIdAfterSelection={executeUpdateForIdAfterSelection}
            isCarrouselCheckboxActive={isCarrouselCheckboxActive}
            isCarrouselScaleActive={isCarrouselScaleActive}
            isCarrouselScaleHorizontalRow={
                isCarrouselScaleActive ? isCarrouselScaleHorizontalRow : undefined
            }
            choiceIndex={choiceIndex}
            choiceCount={choiceCount}
            scaleAnchorLabelIds={scaleAnchorLabelIds}
        />
    );
}

function renderChoice(
    props: Props,
    choice: ChoiceModel,
    isFirst: boolean,
    isLast: boolean,
    key: any,
    choiceIndex: number,
    choiceCount: number,
    scaleAnchorLabelIds?: CarrouselScaleAnchorLabelIds,
    horizontalChoices?: boolean,
    isCarrouselScaleHorizontalRow?: boolean,
) {
    const { question, isCarrouselCheckboxActive } = props;
    switch (question.type) {
        case QuestionType.ImageSelector:
            return renderChoiceListItem(
                props,
                choice,
                isFirst,
                isLast,
                key,
                ImageSelect,
                choiceIndex,
                choiceCount,
            );
        case QuestionType.CheckBox:
            return renderChoiceListItem(
                props,
                choice,
                isFirst,
                isLast,
                key,
                CheckBox,
                choiceIndex,
                choiceCount,
            );
        case QuestionType.Carrousel:
            return renderChoiceListItem(
                props,
                choice,
                isFirst,
                isLast,
                key,
                isCarrouselCheckboxActive ? CheckBox : Radio,
                choiceIndex,
                choiceCount,
                scaleAnchorLabelIds,
                horizontalChoices,
                isCarrouselScaleHorizontalRow,
            );
        default:
            return renderChoiceListItem(
                props,
                choice,
                isFirst,
                isLast,
                key,
                Radio,
                choiceIndex,
                choiceCount,
            );
    }
}

function renderChoices(
    props: Props,
    scaleAnchorLabelIds?: CarrouselScaleAnchorLabelIds,
    horizontalChoices?: boolean,
    isCarrouselScaleHorizontalRow?: boolean,
) {
    const { choices } = props;
    const choiceCount = choices.length;
    return choices.map((choice, i) =>
        renderChoice(
            props,
            choice,
            i === 0,
            i === choiceCount - 1,
            choice.name,
            i,
            choiceCount,
            scaleAnchorLabelIds,
            horizontalChoices,
            isCarrouselScaleHorizontalRow,
        ),
    );
}

function ResetButton(props: Props) {
    const { theme, onReset } = props;
    const question = useQuestion();
    const defaultClearButtonText = useTranslatable(theme, "QuestionClearButton") || "Reset";
    const clearButtonText = useTranslatable(question, "ResetButtonText") || defaultClearButtonText;

    return (
        <Button
            className="button--clear"
            text={clearButtonText}
            size="full"
            onClick={() => onReset()}
        />
    );
}

function CarrouselScaleAnchor({
    id,
    html,
    className,
}: {
    id: string;
    html: string;
    className?: string;
}) {
    return (
        <div
            id={id}
            className={className}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

function renderChoiceListContent(
    props: Props,
    classNames: string,
    scaleAnchorLabelIds?: CarrouselScaleAnchorLabelIds,
    horizontalChoices?: boolean,
    choiceListRef?: React.RefObject<HTMLDivElement | null>,
    isCarrouselScaleHorizontalRow?: boolean,
) {
    return (
        <div ref={choiceListRef} className={classNames}>
            {renderChoices(
                props,
                scaleAnchorLabelIds,
                horizontalChoices,
                isCarrouselScaleHorizontalRow,
            )}
            {shouldDisplayResetButton(props.question) && <ResetButton {...props} />}
        </div>
    );
}

/** Content-sized horizontal rows are centered on all viewports; stretch rows fill the container. */
function shouldCenterCarrouselScaleChoices(
    useVerticalScaleLayout: boolean,
    layoutPlan: CarrouselScaleLayoutPlan | null,
): boolean {
    return !useVerticalScaleLayout && layoutPlan !== null && !layoutPlan.stretchToRow;
}

function renderCarrouselScaleChoices(
    props: Props,
    scaleAnchorLabelIds: CarrouselScaleAnchorLabelIds | undefined,
    scaleChoicesRef: React.RefObject<HTMLDivElement | null>,
    classes: Record<string, string>,
    useVerticalScaleLayout: boolean,
    isMobileDimension: boolean,
    layoutPlan: CarrouselScaleLayoutPlan | null,
) {
    const {
        choices,
        openEndValue,
        onOpenEndChange,
        onOpenEndBlur,
        variable,
        onAfterSelection,
        executeUpdateForIdAfterSelection,
        gridRendering,
    } = props;
    const choiceCount = choices.length;
    const centerScaleChoices = shouldCenterCarrouselScaleChoices(
        useVerticalScaleLayout,
        layoutPlan,
    );

    return (
        <div
            ref={scaleChoicesRef}
            className={classnames(
                classes.carrouselScaleNpsChoices,
                centerScaleChoices && classes.carrouselScaleNpsChoicesCentered,
                useVerticalScaleLayout && classes.carrouselScaleNpsChoicesStacked,
                useVerticalScaleLayout &&
                    isMobileDimension &&
                    classes.carrouselScaleNpsChoicesMobile,
            )}
        >
            {choices.map((choice, i) => {
                const selected = isSelected(props, choice);
                const exclusive = getSettingBoolean(choice, "Exclusive");
                const isDisabled = isChoiceDisabled(props, selected, exclusive, Radio);
                return (
                    <CarrouselScaleItemRenderer
                        key={choice.name}
                        {...getRendererProps(props)}
                        choice={choice}
                        selected={selected}
                        disabled={isDisabled}
                        variable={variable}
                        isFirst={i === 0}
                        isLast={i === choiceCount - 1}
                        openEnd={(openEndValue || {})[choice.index]}
                        onOpenEndChange={onOpenEndChange}
                        onOpenEndBlur={onOpenEndBlur}
                        onAfterSelection={onAfterSelection}
                        executeUpdateForIdAfterSelection={executeUpdateForIdAfterSelection}
                        choiceIndex={i}
                        choiceCount={choiceCount}
                        scaleAnchorLabelIds={scaleAnchorLabelIds}
                        stackedLayout={useVerticalScaleLayout}
                        stretchRow={!useVerticalScaleLayout ? layoutPlan?.stretchToRow : undefined}
                        cellWidth={
                            !useVerticalScaleLayout && !layoutPlan?.stretchToRow
                                ? layoutPlan?.cellWidths[i]
                                : undefined
                        }
                        contentPaddingX={
                            !useVerticalScaleLayout ? layoutPlan?.contentPaddingX : undefined
                        }
                        cellHeight={!useVerticalScaleLayout ? layoutPlan?.cellHeight : undefined}
                        minCellWidthPx={
                            !useVerticalScaleLayout ? layoutPlan?.minCellWidthPx : undefined
                        }
                        gridRendering={gridRendering}
                    />
                );
            })}
            {shouldDisplayResetButton(props.question) && <ResetButton {...props} />}
        </div>
    );
}

export default function ChoiceListRenderer(props: Props) {
    const question = useQuestion();
    const { choices, isCarrouselCheckboxActive, isCarrouselScaleActive, variable, questionnaire, language } =
        props;

    const scaleChoicesRef = React.useRef<HTMLDivElement>(null);
    const [layoutPlan, setLayoutPlan] = React.useState<CarrouselScaleLayoutPlan | null>(null);

    const dimension = useDimensionContext();
    const isMobileDimension = dimension && dimension.maxWidth(480);
    const isTabletDimension = dimension && dimension.maxWidth(768) && !isMobileDimension;
    const isLargeDisplay = dimension && dimension.minWidth(1169);

    const theme = useTheme() as Theme;
    const useModernTheme = shouldUseModernTheme(theme);
    const hasVariableValidationError = useSelector((state: StoreState) =>
        hasChoiceValidationError(
            state.interview?.validation?.errorsByVariableName,
            question,
            variable,
        ),
    );
    const incompleteAnswerHint = stripHtmlForAccessibleName(
        translate(questionnaire ?? question, "IncompleteAnswer", language) || "",
    );
    const displayType = getChoiceListDisplayType(question);

    const numberOfColumns = getSettingNumber(question, "NumberOfColumns");
    const numberOfRows = numberOfColumns > 1 ? Math.ceil(choices?.length / numberOfColumns) : 1;
    const classes = useStyles({
        theme,
        displayType,
        numberOfColumns,
        numberOfRows,
        isCarrouselScaleActive,
        cellHeight: layoutPlan?.cellHeight,
    });

    const leftAnchorHtml = useTranslatable(question, "LeftAnchor");
    const rightAnchorHtml = useTranslatable(question, "RightAnchor");

    const displayAsTable = question.type === QuestionType.ImageSelector;
    const displayOrder = getSettingString(question, "DisplayOrder");

    const isDisplayColumns = displayType === ChoiceListDisplayType.VerticalColumns;
    const isDisplayWide = dimension && dimension.minWidth(778);

    // Internet Explorer fallback layout: vertical columns choice list will be displayed as a simple vertical choice list
    const displayAsGrid = isDisplayColumns && isDisplayWide && !isIE();

    const isHorizontalChoices =
        displayType === ChoiceListDisplayType.Horizontal ||
        displayType === ChoiceListDisplayType.HorizontalColumns;

    React.useLayoutEffect(() => {
        if (!isCarrouselScaleActive || !isHorizontalChoices || !choices?.length) {
            setLayoutPlan(null);
            return undefined;
        }

        const scaleChoicesElement = scaleChoicesRef.current;
        if (!scaleChoicesElement) {
            return undefined;
        }

        const measureLayout = () => {
            const containerWidth =
                scaleChoicesElement.closest(".carrousel-scale")?.clientWidth ??
                scaleChoicesElement.clientWidth;
            if (containerWidth === 0) {
                return;
            }

            const labels = choices.map((choice) =>
                getChoiceLabelPlainText(choice, props.language),
            );
            const computedStyle = window.getComputedStyle(scaleChoicesElement);
            const font =
                computedStyle.font ||
                `${computedStyle.fontSize} ${computedStyle.fontFamily}`.trim();
            const borderWidthPx = theme.BorderWidth ?? 1;

            setLayoutPlan(
                computeCarrouselScaleLayoutPlan({
                    labels,
                    containerWidth,
                    borderWidthPx,
                    font,
                    isMobile: Boolean(isMobileDimension),
                    isTablet: Boolean(isTabletDimension),
                    isLargeDisplay: Boolean(isLargeDisplay),
                }),
            );
        };

        measureLayout();

        const resizeObserver =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(measureLayout)
                : undefined;
        resizeObserver?.observe(scaleChoicesElement);

        window.addEventListener("resize", measureLayout);
        document.fonts?.ready?.then(measureLayout);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", measureLayout);
        };
    }, [
        isCarrouselScaleActive,
        isHorizontalChoices,
        choices,
        props.language,
        dimension?.width,
        dimension?.height,
        isMobileDimension,
        isTabletDimension,
        isLargeDisplay,
        theme.BorderWidth,
    ]);

    React.useLayoutEffect(() => {
        if (
            !isCarrouselScaleActive ||
            !isHorizontalChoices ||
            !layoutPlan ||
            layoutPlan.useVertical
        ) {
            return undefined;
        }

        const scaleChoicesElement = scaleChoicesRef.current;
        if (!scaleChoicesElement || carrouselScaleChoicesDomWrapped(scaleChoicesElement)) {
            setLayoutPlan({
                ...layoutPlan,
                useVertical: true,
                cellWidths: [],
            });
        }

        return undefined;
    }, [isCarrouselScaleActive, isHorizontalChoices, layoutPlan]);

    // DEV-97889: keep this guard AFTER all hooks. Choice Show-if can toggle `choices`
    // empty <-> non-empty between renders; returning early above the useLayoutEffect
    // hooks changes the hook count and crashes React (#310 "Rendered more hooks...").
    if (choices === undefined || choices.length === 0) {
        return null;
    }

    const isScaleLayoutPending =
        isCarrouselScaleActive && isHorizontalChoices && layoutPlan === null;

    const useVerticalScaleLayout =
        !isHorizontalChoices || layoutPlan?.useVertical === true;

    const scaleAnchorsInRowAboveChoices = !useVerticalScaleLayout;
    const scaleAnchorsStackedAroundChoices = useVerticalScaleLayout;

    const role = getRoleForQuestion(question);

    const questionElementId = question?.elementId;
    const hasLeftAnchorText = !isNullOrEmpty(stripHtmlForAccessibleName(leftAnchorHtml ?? ""));
    const hasRightAnchorText = !isNullOrEmpty(stripHtmlForAccessibleName(rightAnchorHtml ?? ""));
    const leftAnchorId =
        questionElementId && hasLeftAnchorText
            ? `${questionElementId}_CAROUSEL_LEFT_ANCHOR`
            : undefined;
    const rightAnchorId =
        questionElementId && hasRightAnchorText
            ? `${questionElementId}_CAROUSEL_RIGHT_ANCHOR`
            : undefined;

    const scaleAnchorLabelIds: CarrouselScaleAnchorLabelIds | undefined =
        isCarrouselScaleActive
            ? { leftId: leftAnchorId, rightId: rightAnchorId }
            : undefined;

    const choiceListClassNames = classnames(
        classes.choiceList,
        displayAsTable && displayOrder === "Vertical" && classes.choiceListTableVertical,
        displayAsTable && displayOrder === "Horizontal" && classes.choiceListTableHorizontal,
        displayAsGrid && classes.choiceListGrid,
        isCarrouselCheckboxActive && classes.checkBoxAlignment,
    );

    if (!isCarrouselScaleActive) {
        return (
            <div role={role} className={choiceListClassNames}>
                {renderChoices(props)}
                {shouldDisplayResetButton(question) && <ResetButton {...props} />}
            </div>
        );
    }

    const useCenteredHorizontalRow = shouldCenterCarrouselScaleChoices(
        useVerticalScaleLayout,
        layoutPlan,
    );

    const horizontalAnchorsAboveChoices =
        scaleAnchorsInRowAboveChoices && (hasLeftAnchorText || hasRightAnchorText) ? (
            <div
                className={
                    useCenteredHorizontalRow
                        ? classes.carrouselScaleAnchorsHorizontalCentered
                        : classes.carrouselScaleAnchorsHorizontal
                }
            >
                {hasLeftAnchorText && leftAnchorId && (
                    <CarrouselScaleAnchor
                        id={leftAnchorId}
                        html={leftAnchorHtml}
                        className={classes.carrouselScaleAnchor}
                    />
                )}
                {hasRightAnchorText && rightAnchorId && (
                    <CarrouselScaleAnchor
                        id={rightAnchorId}
                        html={rightAnchorHtml}
                        className={classes.carrouselScaleAnchor}
                    />
                )}
            </div>
        ) : null;

    return (
        <div
            role={role}
            className={classnames(
                classes.carrouselScaleWrapper,
                isScaleLayoutPending && classes.carrouselScaleLayoutPending,
            )}
            aria-invalid={
                useModernTheme && hasVariableValidationError ? true : undefined
            }
        >
            {useModernTheme && hasVariableValidationError && (
                <button
                    type="button"
                    className={classnames(classes.validationFocusAnchor, "allowFocusOnQuestion")}
                    aria-label={incompleteAnswerHint || "Incomplete answer"}
                />
            )}
            {useCenteredHorizontalRow ? (
                <div className={classes.carrouselScaleHorizontalCentered}>
                    <div className={classes.carrouselScaleHorizontalCenteredInner}>
                        {horizontalAnchorsAboveChoices}
                        {renderCarrouselScaleChoices(
                            props,
                            scaleAnchorLabelIds,
                            scaleChoicesRef,
                            classes,
                            useVerticalScaleLayout,
                            isMobileDimension,
                            layoutPlan,
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {horizontalAnchorsAboveChoices}
                    {scaleAnchorsStackedAroundChoices &&
                        hasLeftAnchorText &&
                        leftAnchorId && (
                            <CarrouselScaleAnchor
                                id={leftAnchorId}
                                html={leftAnchorHtml}
                                className={classnames(
                                    classes.carrouselScaleAnchor,
                                    classes.carrouselScaleAnchorTop,
                                )}
                            />
                        )}
                    {renderCarrouselScaleChoices(
                        props,
                        scaleAnchorLabelIds,
                        scaleChoicesRef,
                        classes,
                        useVerticalScaleLayout,
                        isMobileDimension,
                        layoutPlan,
                    )}
                    {scaleAnchorsStackedAroundChoices &&
                        hasRightAnchorText &&
                        rightAnchorId && (
                            <CarrouselScaleAnchor
                                id={rightAnchorId}
                                html={rightAnchorHtml}
                                className={classnames(
                                    classes.carrouselScaleAnchor,
                                    classes.carrouselScaleAnchorBottom,
                                )}
                            />
                        )}
                </>
            )}
        </div>
    );
}
